import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { AsyncPipe } from '@angular/common';
import type { OnInit } from '@angular/core';
import { Component, DestroyRef, inject, Injector, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import type { OnyxAmountForm } from '@onyx/angular';
import {
  OnyxAutoFocusDirective,
  FormHelper,
  OnyxButtonComponent,
  OnyxDropdownComponent,
  OnyxFormGroupComponent,
  OnyxModalComponent,
  OnyxRadioButtonComponent,
  OnyxTabsComponent,
  OnyxTextareaComponent,
  onyxTextareaLengthValidator,
  OnyxTextFieldComponent,
  OnyxToastService,
} from '@onyx/angular';
import { isEqual } from 'lodash';
import { distinctUntilChanged, map, startWith, Subject } from 'rxjs';
import type { MergeDeep } from 'type-fest';
import { DictionaryCode } from '../../../../../common/enums/dictionary-code';
import { ValidationHelper } from '../../../../../common/helpers/validation.helper';
import type { TelematicsRoutePointOrder } from '../../../../../common/interfaces/telematics/telematics-route-point-order';
import { DictionariesService } from '../../../../../common/services/dictionaries.service';
import { PreferencesService } from '../../../../../common/services/preferences.service';
import { OrderCancellationChargeParty } from '../../enums/order-cancellation-charge-party';
import { OrderCancellationReason } from '../../enums/order-cancellation-reason';
import { OrderHelper } from '../../helpers/order.helper';
import type { Order } from '../../interfaces/order';
import { OrdersService } from '../../services/orders.service';

type OrderCancelFormGroup = ReturnType<OrderCancelModalComponent['buildForm']>;
export type OrderCancelForm = ReturnType<OrderCancelFormGroup['getRawValue']>;

export type OrderCancelFormDto = MergeDeep<
  OrderCancelForm,
  { chargeAmount: OnyxAmountForm | null }
>;

export type OrderCancelModalData = Order | TelematicsRoutePointOrder;

@Component({
  selector: 'app-order-cancel-modal',
  imports: [
    OnyxModalComponent,
    TranslatePipe,
    ReactiveFormsModule,
    OnyxFormGroupComponent,
    OnyxRadioButtonComponent,
    OnyxButtonComponent,
    OnyxTextareaComponent,
    OnyxTabsComponent,
    AsyncPipe,
    OnyxDropdownComponent,
    OnyxTextFieldComponent,
    OnyxAutoFocusDirective,
  ],
  templateUrl: './order-cancel-modal.component.html',
  styleUrl: './order-cancel-modal.component.scss',
})
export class OrderCancelModalComponent implements OnInit {
  protected readonly data = inject<OrderCancelModalData>(DIALOG_DATA);
  protected readonly dialogRef = inject(DialogRef<void>);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly preferencesService = inject(PreferencesService);
  private readonly toastService = inject(OnyxToastService);
  private readonly dictionariesService = inject(DictionariesService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly ordersService = inject(OrdersService);
  private readonly injector = inject(Injector);

  protected readonly I18N = 'orders.orderCancelModal';

  protected readonly OrderCancellationReason = OrderCancellationReason;

  protected readonly orderCancellationChargeParty$ = this.dictionariesService
    .getDictionary(DictionaryCode.ORDER_CANCELLATION_CHARGE_PARTY)
    .pipe(
      map((dictionary) =>
        OrderHelper.isOrderType(this.data) && this.data.outsourcingData
          ? dictionary
          : dictionary.filter(
              (item) => item.value !== OrderCancellationChargeParty.CONTRACTOR,
            ),
      ),
    );
  protected readonly orderCancellationReasons$ =
    this.dictionariesService.getDictionary(
      DictionaryCode.ORDER_CANCELLATION_REASON,
    );
  protected readonly currencies$ = this.dictionariesService.getDictionary(
    DictionaryCode.CURRENCY,
  );

  protected form = this.buildForm();
  protected loading = signal(false);
  protected close$ = new Subject<void>();

  public ngOnInit(): void {
    const form = this.form;
    const controls = form.controls;

    form.valueChanges
      .pipe(
        startWith(undefined),
        map(() => form.getRawValue()),
        distinctUntilChanged((previous, current) => isEqual(previous, current)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((value) => {
        ValidationHelper.toggleRequiredValidator(
          controls.note,
          value.reason === OrderCancellationReason.OTHER,
        );

        ValidationHelper.toggleRequiredValidator(
          controls.chargeAmount,
          !!value.chargeParty &&
            value.chargeParty !== OrderCancellationChargeParty.NONE,
        );

        ValidationHelper.toggleControls(controls.chargeAmount, {
          enabled:
            !!value.chargeParty &&
            value.chargeParty !== OrderCancellationChargeParty.NONE,
          reset: true,
        });
      });
  }

  protected async submit(): Promise<void> {
    if (!(await ValidationHelper.checkValidity(this.form, this.injector))) {
      return;
    }

    const form = this.form.getRawValue();
    const dto: OrderCancelFormDto = {
      ...form,
      chargeAmount:
        form.chargeAmount.value != null
          ? (form.chargeAmount as OnyxAmountForm)
          : null,
    };

    this.loading.set(true);
    this.ordersService
      .cancel(this.data.uuid, dto)
      .subscribe(() => {
        this.toastService.showSuccess({
          message: `${this.I18N}.orderCancelSuccess`,
          keepOnNavigation: true,
        });
        this.close$.next();
      })
      .add(() => this.loading.set(false));
  }

  private buildForm() {
    return this.fb.group({
      reason: this.fb.control<OrderCancellationReason | null>(null, [
        Validators.required,
      ]),
      note: this.fb.control<string | null>(null, [onyxTextareaLengthValidator]),
      chargeParty: this.fb.control<OrderCancellationChargeParty | null>(null, [
        Validators.required,
      ]),
      chargeAmount: FormHelper.buildAmountForm(
        this.fb,
        this.preferencesService,
        { minValue: Number.EPSILON },
      ),
    });
  }
}
